import React, { Suspense, lazy } from 'react';

import ProfileLayout from '@layouts/Profile/ProfileLayout';

import { NotFound } from '../components/shared/NotFound/NotFound';

import { AffiliateProfileRoutes } from '@pages/affiliate/AffiliateProfile/routes';
import { AllPlansRoutes } from '@pages/affiliate/AllPlans/routes';
import { AllProducersRoutes } from '@pages/affiliate/AllProducers/routes';
import { PlanTermsRoutes } from '@pages/affiliate/PlanTerms/routes';
import { ProducerPlansRoutes } from '@pages/affiliate/ProducerPlans/routes';
import { AccessAndSecurityRoutes } from '@pages/dashboard/AccessAndSecurity/routes';
import { AlertsRoutes } from '@pages/dashboard/Alerts/routes';
import { BillingRoutes } from '@pages/dashboard/Billing/routes';
import { ConfirmEmailNotificationRoutes } from '@pages/dashboard/ConfirmEmailNotification/routes';
import { DataAndCommunicationRoutes } from '@pages/dashboard/DataAndCommunication/routes';
import { ForgotPasswordRoutes } from '@pages/dashboard/ForgotPassword/routes';
import { PayOutRoutes } from '@pages/dashboard/PayOut/routes';
import { PolicyInfoAndDashboardRoutes } from '@pages/dashboard/PolicyInfoAndDashboard/routes';
import { GetHelpRoutes, ProfileRoutes } from '@pages/dashboard/Profile/routes';
import { SignInRoutes } from '@pages/dashboard/SignIn/routes';
import { UserType } from '@pages/dashboard/SignUp/SignUp';
import SignUpRoutes from '@pages/dashboard/SignUp/routes';
import { SocialGoodAndDashboardRoutes } from '@pages/dashboard/SocialGoodAndDashboard/routes';
import UserTypeRoutes from '@pages/dashboard/UserType/routes';
import { AboutUsRoutes } from '@pages/landing/AboutUs/routes';
import { BillingInfoRoutes } from '@pages/landing/BillingInfo/routes';
import { CoverageSpecificsRoutes } from '@pages/landing/CoverageSpecifics/routes';
import { DataPrivacyRoutes } from '@pages/landing/DataPrivacy/routes';
import { FAQRoutes } from '@pages/landing/FAQ/routes';
import { FinalConfirmationRoutes } from '@pages/landing/FinalConfirmation/routes';
import { HomeRoutes } from '@pages/landing/Home/routes';
import { IdentityVerifyRoutes } from '@pages/landing/IdentityVerify/routes';
import { IssuingPolicyRoutes } from '@pages/landing/IssuingPolicy/routes';
import { JoinTeamRoutes } from '@pages/landing/JoinTeam/routes';
import { OtherServiceOfferingsRoutes } from '@pages/landing/OtherServiceOfferings/routes';
import PlaceNewPolicyPolicyholderRoutes from '@pages/landing/PlaceNewPolicyPolicyholder/routes';
import { PolicyholderDetailsRoutes } from '@pages/landing/PolicyholderDetails/routes';
import { PolicyholderPropertyDetailsRoutes } from '@pages/landing/PolicyholderPropertyDetails/routes';
import { PricingPlanRoutes } from '@pages/landing/PricingPlan/routes';
import { PublicOutagesMapRoutes } from '@pages/landing/PublicOutagesMap/routes';
import { ReviewCoveragePlanRoutes } from '@pages/landing/ReviewCoveragePlan/routes';
import { RiskExplorationsRoutes } from '@pages/landing/RiskExploration/routes';
import { AllProducerAffiliatesRoutes } from '@pages/producer/AllAffiliates/routes';
import { AllPoliciesRoutes } from '@pages/producer/AllPolicies/routes';
import { BillingInfoProducerRoutes } from '@pages/producer/BillingInfoProducer/routes';
import { IssuingProducerPolicyRoutes } from '@pages/producer/IssuingProducerPolicy/routes';
import PlaceNewPolicyRoutes from '@pages/producer/PlaceNewPolicy/routes';
import { ProducerPolicyholderDetailsRoutes } from '@pages/producer/ProducerPolicyholderDetails/routes';
import { ProducerProfileRoutes } from '@pages/producer/ProducerProfile/routes';
import { PropertyDetailsRoutes } from '@pages/producer/PropertyDetails/routes';
import { ReviewPolicyholderCoveragePlanRoutes } from '@pages/producer/ReviewPolicyholderCoveragePlan/routes';
import SetupProducerRoutes from '@pages/producer/SetupProducer/routes';
import {
  Navigate,
  RouteObject,
  RouterProvider,
  createBrowserRouter,
} from 'react-router-dom';

export const PanelRoutes: RouteObject[] = [
  {
    path: '',
    handle: {
      label: 'Centinel',
      style: {
        color: 'white',
        fontWeight: 'bold',
        fontSize: '1.2em',
      },
    },
    element: (
      <Suspense>
        <Navigate to="/" replace />
      </Suspense>
    ),
  },
  ProfileRoutes,
  AlertsRoutes,
  PolicyInfoAndDashboardRoutes,
  SocialGoodAndDashboardRoutes,
  BillingRoutes,
  PayOutRoutes,
  AccessAndSecurityRoutes,
  DataAndCommunicationRoutes,
  GetHelpRoutes,
];

export const AuthRoutes: RouteObject[] = [
  ...SignInRoutes,
  ...SignUpRoutes,
  ...UserTypeRoutes,
  ...ForgotPasswordRoutes,
  ...ConfirmEmailNotificationRoutes,
];

export const LandingRoutes: RouteObject[] = [
  ...HomeRoutes,
  ...PricingPlanRoutes,
  ...AboutUsRoutes,
  ...IdentityVerifyRoutes,
  ...AboutUsRoutes,
  ...FAQRoutes,
  ...DataPrivacyRoutes,
  ...RiskExplorationsRoutes,
  ...BillingInfoRoutes,
  ...IssuingPolicyRoutes,
  ...ReviewCoveragePlanRoutes,
  ...JoinTeamRoutes,
  ...PolicyholderDetailsRoutes,
  ...FinalConfirmationRoutes,
  ...CoverageSpecificsRoutes,
  ...OtherServiceOfferingsRoutes,
  ...PublicOutagesMapRoutes,
  ...PlaceNewPolicyPolicyholderRoutes,
  ...PolicyholderPropertyDetailsRoutes,
];

export const ProducerRoutes: RouteObject[] = [
  ...ProducerProfileRoutes,
  ...PlaceNewPolicyRoutes,
  ...ProducerPolicyholderDetailsRoutes,
  ...SetupProducerRoutes,
  ...PropertyDetailsRoutes,
  ...AllPoliciesRoutes,
  ...IssuingProducerPolicyRoutes,
  ...ReviewPolicyholderCoveragePlanRoutes,
  ...BillingInfoProducerRoutes,
  ...AllProducerAffiliatesRoutes,
  ...PlanTermsRoutes('producer'),
];

export const AffiliateRoutes: RouteObject[] = [
  ...AffiliateProfileRoutes,
  ...AllProducersRoutes,
  ...AllPlansRoutes,
  ...ProducerPlansRoutes,
  ...PlanTermsRoutes('affiliate'),
];

const LandingLayout = lazy(() => import('@layouts/Landing'));
const PanelLayout = lazy(() => import('@layouts/Panel'));
const AuthLayout = lazy(() => import('@layouts/Auth'));

export const AppRoutes: React.FC = () => {
  const router = createBrowserRouter([
    {
      path: '/',
      element: <LandingLayout />,
      children: LandingRoutes,
    },
    {
      path: '/why-centinel',
      loader: () => {
        window.location.replace('https://www.yourcentinelblog.com/about');
        return null;
      },
    },
    {
      path: '/dashboard',
      element: <PanelLayout />,
      children: PanelRoutes,
      handle: {
        label: 'Home',
      },
    },
    {
      path: '',
      element: <ProfileLayout type={UserType.PRODUCER} />,
      children: ProducerRoutes,
    },
    {
      path: '',
      element: <AuthLayout />,
      children: AuthRoutes,
    },
    {
      path: '',
      element: <ProfileLayout type={UserType.AFFILIATE} />,
      children: AffiliateRoutes,
    },
    {
      path: '*',
      element: <NotFound />,
    },
  ]);

  return (
    <React.Suspense>
      <RouterProvider router={router} />
    </React.Suspense>
  );
};
